import {AppInfoModel, ConfigModel} from "./Models.ts";
import {AddHeadHtml, AddScript, AddStyle, DisableMainSpinner, IsOnPage, SetTopLevelMessage} from "./HtmlUtils.ts";

// To mute warnings about non-existent variables.
declare global {
    const Ext: any;
    const TT: any;
    const Model: any;
    const APPURL: any;
}

let AppInfo: AppInfoModel;
let cspNonce: string;

// If the page wont load in 6 seconds, show a message about slow loading.
const slowLoadMessageTimer = setTimeout(() => SetTopLevelMessage("grey", "Aplikacja ładuje się dłużej niż zwykle, jeśli wkrótce się nie załaduje,\nodśwież stronę lub zobacz czy w konsoli nie ma błędu."), 6000);

fetch("App/Info")
    .then(async response => {
        if (response.status === 200)
            AppInfo = await response.json();
        else
            throw response;
    })
    .then(SetupAppShell)
    // Jeden punkt obsługi błędów całego bootstrapu: i pad App/Info, i pad/timeout
    // ładowania skryptów w SetupAppShell. Bez tego odrzucony promise jest bezpański,
    // a strona wisi na komunikacie "ładuje się dłużej niż zwykle" w nieskończoność.
    .catch(HandleFatalLoadError);

function HandleFatalLoadError(error: unknown): void {
    clearTimeout(slowLoadMessageTimer);
    DisableMainSpinner();
    SetTopLevelMessage("crimson", "Wystąpił problem podczas ładowania aplikacji.\nOdśwież stronę lub sprawdź błąd w konsoli.");
    console.error("Bootstrap aplikacji nie powiódł się:", error);
}

async function SetupAppShell() {
    if (AppInfo.Redirect && location.pathname + location.search !== AppInfo.Redirect) {
        location.href = AppInfo.Redirect;
        return;
    }

    document.documentElement.lang = AppInfo.LanguageCode;
    document.title = AppInfo.ApplicationName;
    cspNonce = (window as any).__csp_nonce__;

    // These take the longest, so start them asap.
    const promises = [
        AddScript(AppInfo.ExtScript, cspNonce),
        LoadConfig()
    ];

    for (const faviconHtml of AppInfo.FaviconTags)
        AddHeadHtml(faviconHtml);

    const stylesLoaded = AppInfo.Styles.map(style => {
        const link = AddStyle(style, cspNonce);
        return new Promise<void>(resolve => {
            if (link.sheet) {
                resolve();
                return;
            }
            // Nie odrzucaj na błędzie - brak CSS nie może blokować startu (i tak jest limit czasu).
            link.addEventListener("load", () => resolve(), { once: true });
            link.addEventListener("error", () => resolve(), { once: true });
        });
    });

    // Zacznij ładować fonty ikon równolegle z ExtJS (czekamy na nie tuż przed renderem UI).
    const iconFontsReady = EnsureIconFontsReady(stylesLoaded);

    // Wait for ExtJs to load, and the Model object to be set (overriden).
    await Promise.all(promises);

    Ext.ns("TT");
    Ext.apply(TT, {
        SignalR: IsOnPage(["Start", "Admin"], AppInfo.AppBase),
        BpmChatIntegration : Model.bpmChatIntegration,
        FacebookIntegration: Model.facebookIntegration,
        MainAppLanguage: "pl",
        IsDesktop: !AppInfo.MobileSupport || Ext.platformTags.desktop,
        IsMobile: AppInfo.MobileSupport && (Ext.platformTags.phone || Ext.platformTags.tablet),
        AFT: Model.Aft ?? AppInfo.Aft, // Model.Aft takes precedence as it could be updated after AppInfo. This happens on the register page.
        APP_BASE: AppInfo.AppBase,
        AppCode: AppInfo.ApplicationCode,
        AppAddress: AppInfo.AppAddress,
        AuthUser: AppInfo.AuthUser,
        AuthUserWithUserCtx : AppInfo.AuthUserWithUserCtx,
        MaxIdentLength: AppInfo.MaxIdentLength,
        MultiLanguageSupport: AppInfo.MultiLanguageSupport,
        Account: AppInfo.Account,
        AppLocale: AppInfo.LanguageCode,
        IsDebug: AppInfo.Debug,
        SessionTimeoutShowTimerBefore: AppInfo.SessionTimeoutShowTimerBefore,
        SessionTimeout: AppInfo.SessionTimeout,
        AiChatEnabled: AppInfo.AiChatEnabled,
        AiChatUrl: AppInfo.AiChatUrl,
        AiChatCollectionGroup: AppInfo.AiChatCollectionGroup,
        AiChatCollectionLabel: AppInfo.AiChatCollectionLabel,
        EnableReminder: AppInfo.EnableReminder,
        EntityMultipleRelationsLoadPostpone: AppInfo.EntityMultipleRelationsLoadPostpone,
        GridsExportXlsxEnabled: AppInfo.GridsExportXlsxEnabled,
        GridsPrintEnabled: AppInfo.GridsPrintEnabled,
        GridsStateful: AppInfo.GridsStateful,
        GridsSummaryRowDockType: AppInfo.GridsSummaryRowDockType,
        ImpersonateAccount: AppInfo.ImpersonateAccount,
        MailProvider: AppInfo.MailProvider,
        SearchByFirst: AppInfo.SearchByFirst,
        UploadFileTypeAllowed: AppInfo.UploadFileTypeAllowed,
        UseLikeInSearch: AppInfo.UseLikeInSearch,
        UserCtxWithAuthUser: AppInfo.UserCtxWithAuthUser,
        WfInstanceCommentEnabled: AppInfo.WfInstanceCommentEnabled,
        SmsSendRange: AppInfo.SmsSendRange
    });

    Ext.ariaWarn = Ext.emptyFn;

    // Nie renderuj UI dopóki fonty ikon nie są gotowe (zwykle już są - ładowały się
    // równolegle z ExtJS). Chroni przed surowymi kodami zamiast ikon przy zimnym/wolnym foncie.
    await iconFontsReady;

    if (AppInfo.CoreBundle) {
        await AddScript(AppInfo.CoreBundle, cspNonce);

        TT.AppThemeUtil.setThemeCssRoot(Model.appThemeCssRoot);

        const stateProvider = new Ext.ux.state.HttpProvider({
            readUrl: APPURL.stateProviderGetUrl,
            saveUrl: APPURL.stateProviderSaveUrl,
            readBaseParams: { cmd: 'readState' },
            saveBaseParams: { cmd: 'saveState' },
            autoRead: false,
            logFailure: false,
            logSuccess: false,
            listeners: {
                statechange: function(stateProvider, name, value) {
                    Model.httpState[name] = value;
                }
            }
        });
        stateProvider.initState(Model.httpState);
        Ext.state.Manager.setProvider(stateProvider);
    }

    if (AppInfo.LocaleScripts?.length > 0)
        await Promise.all(AppInfo.LocaleScripts.map(script => AddScript(script, cspNonce)));

    await Promise.all(AppInfo.Scripts.map(script => AddScript(script, cspNonce)));

    AfterScriptsLoaded();
}

/**
 * Czeka, aż webfonty ikon (FontAwesome, ExtJS, Material Icons) z motywu się załadują, zanim
 * ExtJS wyrenderuje UI. Bez tego glify ikon malują się, zanim webfont dojedzie (font-display:
 * block), i po upływie okna blokady pokazują surowe kody z Private Use Area zamiast ikon.
 *
 * Theme-agnostyczne: pytamy o RODZINY fontów, więc działa dla dowolnego aktywnego motywu
 * (jeśli motyw nie zarejestrował danej rodziny, load() jest no-opem). Z twardym limitem czasu,
 * żeby wolny lub niedostępny font nigdy nie zablokował startu aplikacji.
 */
async function EnsureIconFontsReady(stylesLoaded: Promise<void>[]): Promise<void> {
    const fontSet = (document as any).fonts;
    if (!fontSet?.load)
        return;

    const iconFonts = [
        '900 16px "Font Awesome 5 Free"',         // solid   (x-fa / x-fas)
        '400 16px "Font Awesome 5 Free Regular"', // regular (x-far)
        '400 16px "Font Awesome 5 Brands"',       // brands  (x-fab)
        '400 16px "ExtJS"',                       // wbudowana czcionka glifów ExtJS (drzewa/grid/panele/toolbary)
        '400 16px "Material Icons"',              // ikony encji/menu (.mi-*, IconChooser/IconBrowser)
    ];

    const deadline = new Promise<void>(resolve => setTimeout(resolve, 5000));

    await Promise.race([
        (async () => {
            // @font-face rejestruje się dopiero po sparsowaniu CSS motywu - poczekaj na arkusze.
            await Promise.allSettled(stylesLoaded);
            await Promise.allSettled(iconFonts.map(f => fontSet.load(f)));
        })(),
        deadline,
    ]);
}

function AfterScriptsLoaded()
{
    if (AppInfo.DisableBackspace)
        TT.AppUtil.disableBackSpace();

    // Return if we are on the Login page.
    if (IsOnPage("Login", AppInfo.AppBase)) {
        // Clear the reload timer and the top level message first.
        clearTimeout(slowLoadMessageTimer);
        SetTopLevelMessage("", "");
        return;
    }

    if (AppInfo.Debug) {
        Ext.manifest = {
            compatibility: {
                ext: 7
            }
        };
    }

    if (AppInfo.GridCellSelectable)
        Ext.override(Ext.grid.View, { enableTextSelection: true });

    // Make Account & ImpersonateAccount immutable
    if (TT.Account)
        Object.freeze(TT.Account);

    if (TT.ImpersonateAccount)
        Object.freeze(TT.ImpersonateAccount);

    Ext.onReady(OnExtReady);

    Ext.override(Ext.ux.plugin.PagingResizer, {
        options: AppInfo.GridPagingResizer
    });
}

function OnExtReady() {
    // Clear the reload timer
    clearTimeout(slowLoadMessageTimer);
    SetTopLevelMessage("", "");

    Ext.QuickTips.init();
    Ext.getWin().addListener('beforeunload', Ext.cleanupBeforeUnload, window, { single: true });

    // set global alias to TT.util.AppJavascript singleton
    TT.AppJs = TT.util.AppJavascript;

    // init Application javascripts
    if (Model.appJs)
        TT.AppJs.init(Model.appJs);

    if (AppInfo.ScriptsOnExtReady)
        AppInfo.ScriptsOnExtReady.forEach(script =>
            AddScript(script, cspNonce).catch(e => console.error(`Nie udało się załadować skryptu ${script}:`, e)));
}

async function LoadConfig(): Promise<void> {
    const response = await fetch(AppInfo.ConfigUrl);

    if ((response.status === 401 || response.status === 403) && !location.pathname.endsWith("/Login")) {
        location.href = AppInfo.AppAddress + "Login";
        return;
    }

    if (!response.ok)
        throw new Error(`Failed to load config. Status: ${response.status}`);

    const config = (await response.json()) as ConfigModel;

    OverrideObject(Model, config.Model);
    OverrideObject(APPURL, config.AppUrl);
}

function OverrideObject<T extends object>(target: T, source: Partial<T>): void {
    Object.assign(target, source);
}
